/**
 * enginePreProcessJson_Merge_Slot.ts - Slot component merging for EnginePreProcessJson
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { GenerateHtmlJson } from '../../base/loader/generateHtmlJson.js';
import { mergeDataJsonIntoHtmlJson } from '../../base/engine/htmlJsonMergeUtil.js';
import {
    getValue,
    getKeys,
    getKeysList,
    hasKey,
    convertToString
} from './enginePreProcessJson_Helper.js';
import { TemplateOperation, TemplateOperationKind } from './enginePreProcessJson_Helper.js';
import type { TemplateInfoNode } from './enginePreProcessJson_Helper.js';
import {
    TYPE_HTML,
    TYPE_JSONKEY,
    TYPE_JSONKEY_SKIP,
    TYPE_PLACEHOLDER,
    TYPE_PLACEHOLDER_SKIP,
    TYPE_SLOTHOLDER,
    TYPE_SLOTHOLDER_SKIP,
    TYPE_SLOT,
    TYPE_SLOTHTML,
    META_KEY,
    msToNanos,
    isTypePrefixedKey,
    getFirstHtmlValue
} from './enginePreProcessJson_Merge_Constants.js';
import type { EnginePreProcessJson, ILoaderPreProcessJson } from './enginePreProcessJson.js';
import { type MergeTraversalContext, type PreProcessJsonMergeContext } from './enginePreProcessJson_Merge.js';
import { mergeTemplate } from './enginePreProcessJson_Merge_TemplateTraversal.js';

type PlainJsonObject = { [key: string]: unknown };

function normalizeJsonObject(value: unknown): JsonObject | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    if (value instanceof JsonObject) {
        return value;
    }

    if (value instanceof Map) {
        return new JsonObject(Object.fromEntries(value));
    }

    return new JsonObject(value as PlainJsonObject);
}

/**
 * Merges slot component with slot content injection
 */
export function mergeSlotComponent(
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    context: PreProcessJsonMergeContext,
    slotComponentHtmlJson: JsonObject,
    slotholderData: PlainJsonObject,
    currentTemplate: TemplateInfoNode,
    templateKey: string,
    operations: TemplateOperation[],
    genOperations: boolean,
    jsonData: JsonObject,
    componentHtml: JsonObject | null,
    traversal: MergeTraversalContext
): string {
    const { recursionDepth, flowStack, contextJson, configJson } = traversal;
    const newFlowStack = [...(flowStack || []), currentTemplate.templateName];

    Logger.flow(`MergeSlotComponent: enter templateKey=${templateKey} keys=${slotComponentHtmlJson.size} slotholderKeys=${getKeys(slotholderData).length}`, "PreProcessJson.SlotExpansion");
    const slotContentMap: PlainJsonObject = {};
    for (const slotKey of getKeys(slotholderData)) {
        if (slotKey === 'SlotName' || slotKey === 'IsSkipMarked' || slotKey === META_KEY) continue;

        const slotKeyType = isTypePrefixedKey(slotKey) && slotKey.length > 0 ? slotKey[0] : '\0';
        if (slotKeyType === TYPE_SLOTHTML) continue;

        const slotKeyTypeName = slotKey.includes('_') ? slotKey.substring(slotKey.indexOf('_') + 1) : slotKey;
        if (!slotKeyTypeName.startsWith('SlotHtml')) {
            const slotContentKeyName = GenerateHtmlJson.getKeyName(slotKey) || slotKey;
            slotContentMap[slotContentKeyName] = getValue(slotholderData, slotKey);
        }
    }

    const slotName = getValue(slotholderData, 'SlotName') as string;

    const merged = mergeTemplateWithSlots(
        engine,
        loader,
        context,
        currentTemplate,
        slotComponentHtmlJson,
        jsonData,
        slotContentMap,
        operations,
        genOperations,
        componentHtml,
        { recursionDepth, flowStack: newFlowStack, contextJson, slotName, configJson }
    );

    return merged;
}

function mergeTemplateWithSlots(
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    context: PreProcessJsonMergeContext,
    templateInfo: TemplateInfoNode,
    htmlJson: JsonObject,
    dataJson: JsonObject,
    slotContentMap: PlainJsonObject,
    operations: TemplateOperation[],
    genOperations: boolean,
    componentHtml: JsonObject | null,
    traversal: MergeTraversalContext
): string {
    const appSite = context.appSiteName;
    const metadata = context.metadata;
    const genComponentHtml = context.genComponentHtml;
    const { recursionDepth, flowStack, contextJson, slotName, configJson } = traversal;
    const result: string[] = [];
    const content = htmlJson || {};
    const keysArray = getKeysList(getKeys(content));

    // Canonical phase ordering (matches Normal HTML engine): SlotMerge operations
    // for provided slots emit in slotholder fill order, not component scan order.
    const slotMergeOps = new Map<string, TemplateOperation>();

    for (const key of keysArray) {
        if (key === META_KEY) continue;

        const value = getValue(content, key);
        const keyType = isTypePrefixedKey(key) && key.length > 0 ? key[0] : '\0';
        const keyNameFromKey = GenerateHtmlJson.getKeyName(key);

        if (keyType === TYPE_SLOT) {
            const slotKeyName = keyNameFromKey;
            const slotContent = normalizeJsonObject(getValue(slotContentMap, slotKeyName));
            if (slotContent) {
                Logger.flow(`MergeSlotComponent: slotInjection=${slotKeyName}`, "PreProcessJson.SlotExpansion");
                const slotContentOperations: TemplateOperation[] = [];
                const slotMerged = mergeTemplate(
                    engine,
                    loader,
                    context,
                    templateInfo,
                    slotContent,
                    dataJson,
                    slotContentOperations,
                    genOperations,
                    componentHtml,
                    recursionDepth + 1,
                    flowStack
                );

                result.push(slotMerged);

                const templateNameForKey = slotName || templateInfo.templateName;
                const slotTemplateKey = `${templateInfo.templateAppSite}_${templateNameForKey}`;
                if (operations && genOperations) {
                    slotMergeOps.set(slotKeyName, new TemplateOperation({
                        templateName: slotTemplateKey,
                        kind: TemplateOperationKind.SlotMerge,
                        target: value as string,
                        replacedWith: slotKeyName,
                        operations: slotContentOperations
                    }));
                }
            } else if (typeof value === 'string') {
                result.push(value);
            }
            continue;
        }

        if (keyType === TYPE_JSONKEY_SKIP) {
            const skipKeyName = keyNameFromKey;
            if (operations && genOperations) {
                const templateNameForKey = slotName || templateInfo.templateName;
                const opTemplateName = `${templateInfo.templateAppSite}_${templateNameForKey}`;
                operations.push(new TemplateOperation({
                    templateName: opTemplateName,
                    kind: TemplateOperationKind.KeySkip,
                    target: value as string,
                    replacedWith: `${templateNameForKey}_${skipKeyName}`,
                    operations: []
                }));
            }
            result.push(loader.defaultSkipJson);
            continue;
        }

        if (keyType === TYPE_JSONKEY) {
            const jsonKeyValue = value;
            const keyName = keyNameFromKey;
            const slotContent = normalizeJsonObject(getValue(slotContentMap, keyName));
            if (slotContent) {
                Logger.flow(`MergeSlotComponent: slotJsonKey=${keyName}`, "PreProcessJson.SlotExpansion");
                const slotContentOperations: TemplateOperation[] = [];
                const slotMerged = mergeTemplate(
                    engine,
                    loader,
                    context,
                    templateInfo,
                    slotContent,
                    dataJson,
                    slotContentOperations,
                    genOperations,
                    componentHtml,
                    recursionDepth + 1,
                    flowStack
                );

                result.push(slotMerged);

                const templateNameForKey = slotName || templateInfo.templateName;
                const slotTemplateKey = `${templateInfo.templateAppSite}_${templateNameForKey}`;
                if (operations && genOperations) {
                    slotMergeOps.set(keyName, new TemplateOperation({
                        templateName: slotTemplateKey,
                        kind: TemplateOperationKind.SlotMerge,
                        target: jsonKeyValue as string,
                        replacedWith: keyName,
                        operations: slotContentOperations
                    }));
                }
            } else if (dataJson && hasKey(dataJson, keyName)) {
                result.push(convertToString(getValue(dataJson, keyName)));
            } else {
                result.push(typeof jsonKeyValue === 'string' ? jsonKeyValue : convertToString(jsonKeyValue));
            }
            continue;
        }

        if (keyType === TYPE_HTML || keyType === TYPE_SLOTHTML) {
            result.push(value as string);
        } else if (keyType === TYPE_SLOTHOLDER || keyType === TYPE_SLOTHOLDER_SKIP) {
            const slotholderData = value as PlainJsonObject;
            const slotNameVal = (slotholderData.SlotName as string) || keyNameFromKey;
            const isSkipMarked = keyType === TYPE_SLOTHOLDER_SKIP || slotholderData.IsSkipMarked === 'true';

            if (isSkipMarked) {
                result.push(loader.defaultSkipHtml);
                if (operations && genOperations) {
                    const opTemplateName = `${templateInfo.templateAppSite}_${templateInfo.templateName}`;
                    operations.push(new TemplateOperation({
                        templateName: opTemplateName,
                        kind: TemplateOperationKind.SlotSkip,
                        target: `{{!#${slotNameVal}}}`,
                        replacedWith: `${appSite}_${loader.defaultSkipTemplateName}`,
                        operations: []
                    }));
                }
                continue;
            }

            const component = loader.getComponentFromIndex(appSite, metadata.appView, slotNameVal);
            if (component) {
                const childOperations: TemplateOperation[] = [];
                const componentResult = loader.getTemplateWithView(appSite, templateInfo.templateName, slotNameVal, metadata.appView, flowStack, configJson, null, context.clientMode);
                let slotComponentHtmlJson = componentResult.htmlJson;
                const componentDataJson = componentResult.dataJson;

                const foundKey = `${component.templateAppSite}_${slotNameVal}`;
                const slotDataJson = componentDataJson ?? new JsonObject();
                const mergedSlotComponentHtmlJson = mergeDataJsonIntoHtmlJson(
                    slotComponentHtmlJson!,
                    slotDataJson,
                    foundKey,
                    slotNameVal,
                    childOperations,
                    loader.defaultReplaceHtml,
                    false,
                    contextJson
                );
                const mergeJsonData = getKeys(slotDataJson).length > 0 ? slotDataJson : dataJson;

                if (genComponentHtml && componentHtml) {
                    componentHtml.set(slotNameVal, '');
                }

                const slotMerged = mergeSlotComponent(
                    engine,
                    loader,
                    context,
                    mergedSlotComponentHtmlJson,
                    slotholderData,
                    component,
                    foundKey,
                    childOperations,
                    genOperations,
                    mergeJsonData,
                    componentHtml,
                    { recursionDepth, flowStack, contextJson, slotName: null, configJson }
                );
                result.push(slotMerged);

                if (genComponentHtml && componentHtml) {
                    componentHtml.set(slotNameVal, slotMerged);
                }

                if (operations && genOperations) {
                    const parentTemplateName = templateInfo.placeholderName || templateInfo.templateName;
                    const opTemplateName = `${templateInfo.templateAppSite}_${parentTemplateName}`;
                    operations.push(new TemplateOperation({
                        templateName: opTemplateName,
                        kind: TemplateOperationKind.SlotReplace,
                        target: `{{#${slotNameVal}}}`,
                        replacedWith: foundKey,
                        operations: childOperations
                    }));
                }
            }
        } else if (keyType === TYPE_PLACEHOLDER || keyType === TYPE_PLACEHOLDER_SKIP) {
            const placeholderName = keyNameFromKey || (typeof value === 'string' ? value.substring(2, value.length - 2) : '');

            if (placeholderName.startsWith('!$')) {
                const keyName = placeholderName.substring(2);
                result.push(loader.defaultSkipJson);
                if (operations && genOperations) {
                    const parentTemplateName = templateInfo.placeholderName || templateInfo.templateName;
                    const opTemplateName = `${templateInfo.templateAppSite}_${parentTemplateName}`;
                    operations.push(new TemplateOperation({
                        templateName: opTemplateName,
                        kind: TemplateOperationKind.KeySkip,
                        target: value as string,
                        replacedWith: `${parentTemplateName}_${keyName}`,
                        operations: []
                    }));
                }
                continue;
            }

            const component = loader.getComponentFromIndex(appSite, metadata.appView, placeholderName);
            if (component) {
                const componentTemplateName = component.templateName || placeholderName;
                const newFlowStack = [...(flowStack || []), componentTemplateName];
                const componentResult = loader.getTemplateWithView(appSite, componentTemplateName, componentTemplateName, metadata.appView, newFlowStack, configJson, null, context.clientMode);
                if (componentResult && componentResult.htmlJson) {
                    if (genComponentHtml && componentHtml) {
                        componentHtml.set(placeholderName, '');
                    }

                    const componentMerged = mergeTemplate(
                        engine,
                        loader,
                        context,
                        component,
                        componentResult.htmlJson,
                        componentResult.dataJson ?? new JsonObject(),
                        operations,
                        genOperations,
                        componentHtml,
                        recursionDepth + 1,
                        newFlowStack
                    );
                    result.push(componentMerged);

                    if (genComponentHtml && componentHtml) {
                        componentHtml.set(placeholderName, componentMerged);
                    }
                }
            } else {
                const newFlowStack = [...(flowStack || []), placeholderName];
                const startGetCompFallback2 = performance.now();
                const componentResult = loader.getTemplateWithView(appSite, templateInfo.templateName, placeholderName, metadata.appView, newFlowStack, configJson, null, context.clientMode);
                engine._getTemplateNanos += msToNanos(performance.now() - startGetCompFallback2);
                engine._getTemplateCount++;

                if (componentResult && componentResult.htmlJson) {
                    const isSkip = componentResult.resolvedTemplateName === loader.defaultSkipTemplateName;
                    const isMissing = componentResult.resolvedTemplateName === loader.defaultReplaceTemplateName;

                    if (isSkip || isMissing || getKeys(componentResult.htmlJson).length === 1) {
                        result.push(getFirstHtmlValue(componentResult.htmlJson));
                    } else {
                        const componentMerged = mergeTemplate(
                            engine,
                            loader,
                            context,
                            templateInfo,
                            componentResult.htmlJson,
                            componentResult.dataJson ?? new JsonObject(),
                            operations,
                            genOperations,
                            componentHtml,
                            recursionDepth + 1,
                            newFlowStack
                        );
                        result.push(componentMerged);
                    }
                } else {
                    result.push(value as string);
                }
            }
        } else if (typeof value === 'object' && value !== null) {
            const nested = mergeTemplateWithSlots(
                engine,
                loader,
                context,
                templateInfo,
                new JsonObject(value as PlainJsonObject),
                dataJson,
                slotContentMap,
                operations,
                genOperations,
                componentHtml,
                { recursionDepth: recursionDepth + 1, flowStack, contextJson, slotName, configJson }
            );
            result.push(nested);
        } else {
            result.push(value as string);
        }
    }

    if (operations && genOperations && slotMergeOps.size > 0) {
        for (const slotholderKey of Object.keys(slotContentMap)) {
            const slotMergeOp = slotMergeOps.get(slotholderKey);
            if (slotMergeOp) {
                operations.push(slotMergeOp);
            }
        }
    }

    return result.join('');
}
